<template>
  <Content class="goodsBox">
    <Alert type="success" show-icon class="today-stats-alert" v-if="todayStats">
      <span>
        今日净营业额 <b>￥{{ todayStats.today_revenue_yuan.toFixed(2) }}</b>
        ，已支付订单 {{ todayStats.today_order_count }} 笔
        ，待确认 {{ todayStats.pending_confirm }} 笔
      </span>
      <a slot="action" @click="loadTodayStats">刷新</a>
    </Alert>
    <Form id="searchForm" :model="formItem" :label-width="80" inline>
      <FormItem label="关键字">
        <Input
          v-model="formItem.key"
          placeholder="商品、订单号、用户昵称/手机"
          clearable
          style="width: 260px;"
        />
      </FormItem>
      <FormItem label="订单状态">
        <Select
          v-model="formItem.deliveryStatus"
          clearable
          placeholder="全部"
          style="width: 160px;"
        >
          <Option v-for="item in statusFilterOptions" :key="item.value" :value="item.value">
            {{ item.label }}
          </Option>
        </Select>
      </FormItem>
      <FormItem :label-width="20">
        <Button type="primary" @click="search">查询</Button>
        <Button style="margin-left: 8px" @click="clear">重置</Button>
      </FormItem>
    </Form>
    <Table
      :columns="columns"
      :data="tableData"
      :height="tablesHeight"
      border
    ></Table>
    <Page
      class="table_page_css"
      v-show="total"
      :total="total"
      :current="searchInfo.pageNo"
      :page-size="searchInfo.pageSize"
      :page-size-opts="pageSizeOpts"
      @on-change="pageNoChange"
      @on-page-size-change="pageSizeChange"
      show-sizer
      show-elevator
      show-total
    />

    <Modal
      v-model="refundModalVisible"
      :title="refundModalTitle"
      :loading="refundOkLoading"
      :mask-closable="false"
      @on-ok="submitRefundModal"
      @on-cancel="closeRefundModal"
    >
      <p style="margin-bottom: 12px; color: #515a6e;">
        实付可退 <b>￥{{ refundMaxYuan }}</b>，可输入全额或部分退款金额。
      </p>
      <p v-if="refundPayHint" style="margin-bottom: 12px; color: #808695; font-size: 13px;">
        {{ refundPayHint }}
      </p>
      <Form :label-width="90">
        <FormItem label="退款金额">
          <InputNumber
            v-model="refundYuan"
            :min="0.01"
            :max="Number(refundMaxYuan) || 0.01"
            :step="0.01"
            :precision="2"
            style="width: 180px;"
          />
          <span style="margin-left: 8px;">元</span>
          <a style="margin-left: 12px;" @click="fillFullRefund">全额</a>
        </FormItem>
        <FormItem label="退款说明" required>
          <Input
            v-model="refundNote"
            type="textarea"
            :rows="3"
            :maxlength="200"
            show-word-limit
            placeholder="必填，请说明退款原因"
          />
        </FormItem>
      </Form>
    </Modal>

    <Modal
      v-model="detailModalVisible"
      title="订单详情"
      width="560"
      :mask-closable="true"
      footer-hide
    >
      <div v-if="detailRow" class="order-detail">
        <div class="info-item">
          <span class="label">订单状态：</span>
          <span class="value"><Tag :color="detailStatusColor">{{ detailStatusText }}</Tag></span>
        </div>
        <div class="info-item">
          <span class="label">下单时间：</span>
          <span class="value">{{ detailRow.createtime || detailRow.pay_time || '-' }}</span>
        </div>
        <div class="info-item">
          <span class="label">订单号：</span>
          <span class="value">
            {{ detailOrderId }}
            <a style="margin-left: 8px;" @click="copyDetailOrderId">复制</a>
          </span>
        </div>
        <div class="info-item">
          <span class="label">商品：</span>
          <span class="value">
            <div v-for="(g, gi) in detailGoodsLines" :key="'g' + gi" class="goods-line">
              <span>{{ g.name }}{{ g.spec ? `（${g.spec}）` : '' }} ×{{ g.qty }}</span>
              <span v-if="g.amountText" class="goods-line__amt">￥{{ g.amountText }}</span>
            </div>
            <span v-if="!detailGoodsLines.length">{{ detailProductText }}</span>
          </span>
        </div>
        <div class="info-item">
          <span class="label">下单用户：</span>
          <span class="value">{{ detailCustomerText || '-' }}</span>
        </div>
        <div class="info-item">
          <span class="label">就餐方式：</span>
          <span class="value">
            {{ detailFulfillmentLabel || '-' }}
            <template v-if="detailPickupCode"> · 取餐码 {{ detailPickupCode }}</template>
          </span>
        </div>
        <div class="info-item">
          <span class="label">履约备注：</span>
          <span class="value">{{ detailRemarkText || '-' }}</span>
        </div>

        <Divider size="small" style="margin: 12px 0;" />

        <div class="pay-section-title">支付明细</div>
        <div
          v-for="(p, pi) in detailPayBreakdown"
          :key="'p' + pi"
          class="info-item"
          :class="{ 'info-item--em': p.emphasize }"
        >
          <span class="label">{{ p.label }}：</span>
          <span class="value" :class="p.valueClass">{{ p.value }}</span>
        </div>
        <div v-if="!detailPayBreakdown.length" class="info-item">
          <span class="label">实付金额：</span>
          <span class="value">￥{{ detailAmountText }}</span>
        </div>
        <div class="info-item" v-if="detailConfirmedRefundAmount">
          <span class="label">已退金额：</span>
          <span class="value" style="color: #ed4014;">￥{{ detailConfirmedRefundAmount }}</span>
        </div>
        <div class="info-item" v-if="detailReservedRefundText">
          <span class="label">退款处理中：</span>
          <span class="value" style="color: #ff9900;">￥{{ detailReservedRefundText }}</span>
        </div>
        <div class="info-item" v-if="detailRemainRefundText">
          <span class="label">可退余额：</span>
          <span class="value">￥{{ detailRemainRefundText }}</span>
        </div>

        <Divider size="small" style="margin: 12px 0;" />

        <div class="info-item">
          <span class="label">用户评价：</span>
          <span class="value">{{ detailReviewText || '暂无评价' }}</span>
        </div>
        <div class="info-item">
          <span class="label">评价时间：</span>
          <span class="value">{{ detailReviewTime || '-' }}</span>
        </div>

        <Divider size="small" style="margin: 12px 0;" />

        <div class="info-item">
          <span class="label">退款说明：</span>
          <span class="value">{{ detailRefundNote || '-' }}</span>
        </div>
        <div class="info-item">
          <span class="label">退款时间：</span>
          <span class="value">{{ detailRefundTime || '-' }}</span>
        </div>
        <div class="info-item">
          <span class="label">退款金额：</span>
          <span class="value" :style="detailRefundActionAmount ? 'color:#ed4014;' : ''">{{ detailRefundActionAmount ? `￥${detailRefundActionAmount}` : '-' }}</span>
        </div>
        <div class="info-item" v-if="detailRefundResultText">
          <span class="label">退款结果：</span>
          <span class="value">
            <Tag :color="detailRefundResultColor">{{ detailRefundResultText }}</Tag>
          </span>
        </div>
        <div class="info-item" v-if="detailRefundRecordsText">
          <span class="label">退款流水：</span>
          <span class="value" style="white-space: pre-line;">{{ detailRefundRecordsText }}</span>
        </div>
      </div>
    </Modal>
  </Content>
</template>
<script>
import {
  getfoodOrderList,
  adminConfirmFoodOrder,
  adminPickingDoneFoodOrder,
  adminCancelAndRefundFoodOrder,
  adminRefundFoodOrder,
  adminCancelApproveFoodOrder,
  adminCancelRejectFoodOrder,
  updateordertype,
  adminReprintFoodOrder,
} from '@/api/foodorder'
import { getFoodTodayStats } from '@/api/home'

const DELIVERY_STATUS_MAP = {
  PENDING_PAYMENT: '待支付',
  PENDING_CONFIRM: '待确认',
  PREPARING: '备货中',
  WAIT_DISPATCH: '待分配',
  DISPATCHING: '派送中',
  WAIT_PICKUP: '待取货',
  RETURNING_DEPOT: '返仓中',
  COMPLETED: '已完成',
  PARTIAL_REFUNDED: '部分退款',
  REFUNDED: '已退款',
  CANCELLED: '已取消',
  WAIT_MERCHANT: '待确认',
  CONFIRMED: '备货中',
  READY_ASSIGN: '待分配',
  EN_ROUTE: '派送中',
  ASSIGNED: '派送中',
  ARRIVED: '待取货',
}

function formatItemLines(row) {
  const oi = row && row.order_items
  if (Array.isArray(oi) && oi.length > 0) {
    return oi
      .filter((it) => it && typeof it === 'object')
      .map((it) => {
        const name =
          (it.food_name != null && String(it.food_name).trim() !== ''
            ? it.food_name
            : it.name) || '商品'
        const q = Number(it.quantity)
        const qty = Number.isFinite(q) && q > 0 ? Math.floor(q) : 1
        return { name: String(name).trim(), qty }
      })
  }
  const title =
    (row.food_name && String(row.food_name).trim()) ||
    (row.foodDetails && row.foodDetails.name) ||
    '商品'
  const qn = row && row.num != null ? Number(row.num) : NaN
  const qty = Number.isFinite(qn) && qn > 0 ? Math.floor(qn) : 1
  return [{ name: String(title), qty }]
}

function formatProductSummary(row) {
  const lines = formatItemLines(row)
  if (!lines.length) return '—'
  return lines.map((l) => `${l.name} ×${l.qty}`).join('、')
}

function formatPayAmountFen(row) {
  const bal = Math.max(0, Math.round(Number(row && row.balance_pay_fen) || 0))
  const wx = Math.max(
    0,
    Math.round(
      Number(
        row && (row.wx_pay_fen != null ? row.wx_pay_fen : row.wx_total_fen)
      ) || 0
    )
  )
  let total = bal + wx
  if (total <= 0) {
    const pm = row && row.pay_mes
    if (pm && pm.amount != null && !Number.isNaN(Number(pm.amount))) {
      total = Math.max(0, Math.round(Number(pm.amount)))
    }
  }
  return total
}

/** 单笔流水：已确认入账（success 全额；processing 仅余额；无状态视为历史成功） */
function recordConfirmedRefundFen(r) {
  if (!r) return 0
  const st = String(r.refund_result_status || '')
    .trim()
    .toLowerCase()
  const wx = Math.max(0, Math.round(Number(r.wx_refund_fen) || 0))
  const bal = Math.max(0, Math.round(Number(r.balance_refund_fen) || 0))
  const rf = Math.max(0, Math.round(Number(r.refund_fen) || 0))
  if (st === 'success') return rf > 0 ? rf : wx + bal
  if (st === 'processing' || st === 'closed' || st === 'abnormal') return bal
  if (!st || st === 'none') return rf > 0 ? rf : wx + bal
  return 0
}

function recordReservedRefundFen(r) {
  if (!r) return 0
  const st = String(r.refund_result_status || '')
    .trim()
    .toLowerCase()
  if (st !== 'processing') return 0
  return Math.max(0, Math.round(Number(r.wx_refund_fen) || 0))
}

function formatRefundedTotalFen(row) {
  if (!row) return 0
  let sum = 0
  const recs = row.refund_records
  if (Array.isArray(recs)) {
    for (let i = 0; i < recs.length; i++) sum += recordConfirmedRefundFen(recs[i])
  }
  if (row.delivery_cancel_request) {
    sum += recordConfirmedRefundFen(row.delivery_cancel_request)
  }
  if (sum > 0) return sum
  const stored = Math.round(Number(row.refunded_total_fen) || 0)
  if (Number.isFinite(stored) && stored > 0) return stored
  const lastSt = String(row.last_refund_result_status || '')
    .trim()
    .toLowerCase()
  if (lastSt === 'processing' || lastSt === 'closed' || lastSt === 'abnormal') return 0
  const last = Math.round(Number(row.last_refund_fen || row.refund_fen || row.refunded_fen) || 0)
  return Number.isFinite(last) && last > 0 ? last : 0
}

function formatReservedRefundFen(row) {
  if (!row) return 0
  let sum = 0
  const recs = row.refund_records
  if (Array.isArray(recs)) {
    for (let i = 0; i < recs.length; i++) sum += recordReservedRefundFen(recs[i])
  }
  if (row.delivery_cancel_request) {
    sum += recordReservedRefundFen(row.delivery_cancel_request)
  }
  return sum
}

function formatRemainRefundableFen(row) {
  const paid = formatPayAmountFen(row)
  const refunded = formatRefundedTotalFen(row)
  const reserved = formatReservedRefundFen(row)
  return Math.max(0, paid - refunded - reserved)
}

function formatPayAmount(row) {
  const total = formatPayAmountFen(row)
  if (total > 0) return (total / 100).toFixed(2)
  return '—'
}

function formatPayAmountHint(row) {
  const bal = Math.max(0, Math.round(Number(row && row.balance_pay_fen) || 0))
  const wx = Math.max(
    0,
    Math.round(
      Number(
        row && (row.wx_pay_fen != null ? row.wx_pay_fen : row.wx_total_fen)
      ) || 0
    )
  )
  if (bal > 0 && wx > 0) {
    return `含余额￥${(bal / 100).toFixed(2)} + 微信￥${(wx / 100).toFixed(2)}`
  }
  if (bal > 0) return '余额支付，取消后退回余额'
  if (wx > 0) return '微信支付，取消后原路退回微信'
  return ''
}

function fenToYuanText(fen) {
  const n = Math.round(Number(fen) || 0)
  return (n / 100).toFixed(2)
}

function pickFen(row, keys) {
  if (!row) return 0
  for (let i = 0; i < keys.length; i++) {
    const v = row[keys[i]]
    if (v != null && v !== '' && Number.isFinite(Number(v))) {
      return Math.max(0, Math.round(Number(v)))
    }
  }
  return 0
}

function formatGoodsDetailLines(row) {
  const oi = row && row.order_items
  if (Array.isArray(oi) && oi.length > 0) {
    return oi
      .filter((it) => it && typeof it === 'object')
      .map((it) => {
        const name =
          (it.food_name != null && String(it.food_name).trim() !== ''
            ? it.food_name
            : it.name) || '商品'
        const q = Number(it.quantity != null ? it.quantity : it.num)
        const qty = Number.isFinite(q) && q > 0 ? Math.floor(q) : 1
        const spec =
          it.spec_label != null && String(it.spec_label).trim() !== ''
            ? String(it.spec_label).trim()
            : ''
        let amountText = ''
        const unit = Number(it.unit_price)
        if (Number.isFinite(unit) && unit >= 0) {
          amountText = (unit * qty).toFixed(2)
        } else if (it.line_fen != null && Number.isFinite(Number(it.line_fen))) {
          amountText = fenToYuanText(it.line_fen)
        }
        return { name: String(name).trim(), qty, spec, amountText }
      })
  }
  const lines = formatItemLines(row)
  return lines.map((l) => ({ name: l.name, qty: l.qty, spec: '', amountText: '' }))
}

/** 订单支付明细：小计 / 各类优惠 / 实付构成 */
function buildPayBreakdown(row) {
  if (!row) return []
  const list = []
  const subtotalFen = pickFen(row, [
    'order_subtotal_fen',
    'pending_list_fen',
    'list_fen',
  ])
  let goodsFen = subtotalFen
  if (!goodsFen && Array.isArray(row.order_items)) {
    goodsFen = row.order_items.reduce((s, it) => {
      if (!it) return s
      if (it.line_fen != null && Number.isFinite(Number(it.line_fen))) {
        return s + Math.round(Number(it.line_fen))
      }
      const unit = Number(it.unit_price)
      const q = Number(it.quantity != null ? it.quantity : it.num)
      const qty = Number.isFinite(q) && q > 0 ? Math.floor(q) : 1
      if (Number.isFinite(unit) && unit >= 0) return s + Math.round(unit * 100) * qty
      return s
    }, 0)
  }

  const memberFen = pickFen(row, ['member_discount_fen', 'pending_member_discount_fen'])
  const cardFen = pickFen(row, ['card_discount_fen', 'pending_card_discount_fen'])
  const couponFen = pickFen(row, ['coupon_discount_fen', 'pending_coupon_discount_fen'])
  const balFen = pickFen(row, ['balance_pay_fen', 'pending_balance_pay_fen'])
  let wxFen = pickFen(row, ['wx_pay_fen', 'wx_total_fen', 'pending_wx_pay_fen', 'pending_pay_fen'])
  if (!wxFen && row.pay_mes && row.pay_mes.amount != null) {
    wxFen = Math.max(0, Math.round(Number(row.pay_mes.amount) || 0))
  }

  const cardName =
    (row.benefit_card_name && String(row.benefit_card_name).trim()) ||
    (row.pending_benefit_card_name && String(row.pending_benefit_card_name).trim()) ||
    ''

  if (goodsFen > 0) {
    list.push({ label: '商品小计', value: `￥${fenToYuanText(goodsFen)}` })
  }
  if (memberFen > 0) {
    list.push({
      label: '会员价优惠',
      value: `-￥${fenToYuanText(memberFen)}`,
      valueClass: 'value--disc',
    })
  }
  if (cardFen > 0) {
    list.push({
      label: cardName ? `权益卡优惠（${cardName}）` : '权益卡优惠',
      value: `-￥${fenToYuanText(cardFen)}`,
      valueClass: 'value--disc',
    })
  }
  if (couponFen > 0) {
    list.push({
      label: '优惠券减免',
      value: `-￥${fenToYuanText(couponFen)}`,
      valueClass: 'value--disc',
    })
  }
  if (balFen > 0) {
    list.push({
      label: '余额支付',
      value: `￥${fenToYuanText(balFen)}`,
    })
  }
  if (wxFen > 0) {
    list.push({ label: '微信支付', value: `￥${fenToYuanText(wxFen)}` })
  }

  const paidFen = balFen + wxFen
  if (paidFen > 0) {
    list.push({
      label: '实付合计',
      value: `￥${fenToYuanText(paidFen)}`,
      emphasize: true,
      valueClass: 'value--total',
    })
  } else if (goodsFen > 0) {
    const afterDisc = Math.max(0, goodsFen - memberFen - cardFen - couponFen)
    list.push({
      label: '应付合计',
      value: `￥${fenToYuanText(afterDisc)}`,
      emphasize: true,
      valueClass: 'value--total',
    })
  }
  return list
}

function shortId(id) {
  if (id == null || id === '') return '—'
  const s = String(id)
  if (s.length <= 14) return s
  return `${s.slice(0, 6)}…${s.slice(-6)}`
}

function orderStatusText(row) {
  if (row && row.status_label) return row.status_label
  const ds = row && row.delivery_status
  if (ds === 'REFUNDED' || ds === 'PARTIAL_REFUNDED') {
    return DELIVERY_STATUS_MAP[ds] || ds
  }
  // 兼容旧数据：完成后退款未改状态
  if (ds === 'COMPLETED') {
    const refunded = formatRefundedTotalFen(row)
    if (refunded > 0) {
      const paid = formatPayAmountFen(row)
      if (paid > 0 && refunded >= paid) return '已退款'
      return '部分退款'
    }
  }
  if (ds) {
    if (isDineInOrder(row) && (ds === 'PREPARING' || ds === 'CONFIRMED')) {
      return '制作中'
    }
    return DELIVERY_STATUS_MAP[ds] || ds
  }
  const typ = Number(row && row.type)
  if (typ === 1) return '已下单'
  if (typ === 2) return '运输中'
  if (typ === 3) return '已送达'
  return '—'
}

function orderStatusTagColor(row) {
  if (!row) return 'default'
  const cancelPending =
    row.delivery_cancel_request &&
    (row.delivery_cancel_request.status === 'pending' ||
      row.delivery_cancel_request.status === 'processing')
  if (cancelPending) return 'warning'
  const ds = String(row.delivery_status || '')
  if (ds === 'CANCELLED') return 'default'
  if (ds === 'REFUNDED' || ds === 'PARTIAL_REFUNDED') return 'error'
  if (ds === 'COMPLETED') {
    if (formatRefundedTotalFen(row) > 0) return 'error'
    return 'success'
  }
  if (Number(row.type) === 3) return 'success'
  if (ds === 'PENDING_PAYMENT') return 'orange'
  return 'primary'
}

function isDineInOrder(row) {
  const m = row && String(row.fulfillment_mode || '').trim()
  return m === 'dine_in' || m === 'pack'
}

function formatFulfillmentMode(row) {
  const mode = row && String(row.fulfillment_mode || '').trim()
  const no = row && row.table_no != null ? String(row.table_no).trim() : ''
  const pickup = row && row.pickup_code != null ? String(row.pickup_code).trim() : ''
  const pickupSub = pickup ? `取餐码 ${pickup}` : ''
  if (mode === 'dine_in') {
    const parts = []
    if (no) parts.push(`${no} 号桌`)
    if (pickupSub) parts.push(pickupSub)
    return { label: '堂食', sub: parts.join(' · ') }
  }
  if (mode === 'pack') {
    const parts = []
    if (no) parts.push(`${no} 号桌`)
    if (pickupSub) parts.push(pickupSub)
    return { label: '打包', sub: parts.join(' · ') }
  }
  return { label: '其它', sub: pickupSub }
}

/** 履约备注：用户下单填写的 remark */
function fulfillmentRemarkText(row) {
  if (!row) return ''
  return String(row.remark || row.note || row.user_note || row.bz || '').trim()
}

function formatUserReview(row) {
  const pj = row && row.pj
  if (!pj || typeof pj !== 'object') return ''
  const score = pj.score != null ? Number(pj.score) : NaN
  const content = pj.content != null ? String(pj.content).trim() : ''
  const scoreText = Number.isFinite(score) && score > 0 ? `${score}星` : ''
  if (scoreText && content) return `${scoreText}：${content}`
  return scoreText || content || ''
}

/** 订单上的退款说明（字段 refund_note，或取消/退款记录里的说明） */
function formatRefundNote(row) {
  if (!row) return ''
  const top = row.refund_note != null ? String(row.refund_note).trim() : ''
  if (top) return top
  const cr = row.delivery_cancel_request
  if (cr) {
    const fromCr =
      (cr.refund_note != null && String(cr.refund_note).trim()) ||
      (cr.reason != null && String(cr.reason).trim()) ||
      ''
    if (fromCr) return fromCr
  }
  const recs = row.refund_records
  if (Array.isArray(recs) && recs.length) {
    const last = recs[recs.length - 1]
    if (last && last.note != null && String(last.note).trim()) {
      return String(last.note).trim()
    }
  }
  return ''
}

function formatDateTime(val) {
  if (val == null || val === '') return ''
  if (typeof val === 'string') {
    const s = val.trim()
    if (!s) return ''
    // 已是可读字符串
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 19).replace('T', ' ')
  }
  const d = val instanceof Date ? val : new Date(val)
  if (Number.isNaN(d.getTime())) return String(val)
  const pad = (n) => (n < 10 ? `0${n}` : `${n}`)
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

function formatReviewTime(row) {
  const pj = row && row.pj
  if (!pj || typeof pj !== 'object') return ''
  return formatDateTime(pj.createTime || pj.created_at || pj.createtime || '')
}

function formatRefundTime(row) {
  if (!row) return ''
  const candidates = [
    row.refund_note_at,
    row.last_refund_at,
    row.refunded_at,
    row.cancelled_at,
  ]
  const cr = row.delivery_cancel_request
  if (cr) {
    candidates.push(cr.refunded_at, cr.cancelled_at, cr.processed_at, cr.updated_at)
  }
  const recs = row.refund_records
  if (Array.isArray(recs) && recs.length) {
    const last = recs[recs.length - 1]
    if (last) candidates.push(last.refunded_at, last.created_at, last.at)
  }
  for (const c of candidates) {
    const t = formatDateTime(c)
    if (t) return t
  }
  return ''
}

function formatRefundAmount(row) {
  if (!row) return ''
  const total = formatRefundedTotalFen(row)
  if (total > 0) return (total / 100).toFixed(2)
  return ''
}

/**
 * 详情「退款金额」：取本次/取消退款发起金额（含处理中），不局限于已确认入账
 */
function formatRefundActionAmount(row) {
  if (!row) return ''
  const pickFen = (r) => {
    if (!r) return 0
    const rf = Math.max(0, Math.round(Number(r.refund_fen) || 0))
    const wx = Math.max(0, Math.round(Number(r.wx_refund_fen) || 0))
    const bal = Math.max(0, Math.round(Number(r.balance_refund_fen) || 0))
    return rf > 0 ? rf : wx + bal
  }
  const cr = row.delivery_cancel_request
  if (cr) {
    const fen = pickFen(cr)
    if (fen > 0) return (fen / 100).toFixed(2)
  }
  const recs = row.refund_records
  if (Array.isArray(recs) && recs.length) {
    const last = recs[recs.length - 1]
    const fen = pickFen(last)
    if (fen > 0) return (fen / 100).toFixed(2)
  }
  const last = Math.max(0, Math.round(Number(row.last_refund_fen || row.refund_fen || row.refunded_fen) || 0))
  if (last > 0) return (last / 100).toFixed(2)
  // 兜底：已确认累计 / 处理中占用
  const confirmed = formatRefundedTotalFen(row)
  if (confirmed > 0) return (confirmed / 100).toFixed(2)
  const reserved = formatReservedRefundFen(row)
  if (reserved > 0) return (reserved / 100).toFixed(2)
  return ''
}

function formatReservedRefundAmount(row) {
  if (!row) return ''
  const fen = formatReservedRefundFen(row)
  if (fen > 0) return (fen / 100).toFixed(2)
  return ''
}

function formatRefundRecordsText(row) {
  const recs = row && row.refund_records
  if (!Array.isArray(recs) || !recs.length) return ''
  return recs
    .map((r, idx) => {
      if (!r) return ''
      const fen = Math.max(0, Math.round(Number(r.refund_fen) || 0))
      const amt = fen > 0 ? `￥${(fen / 100).toFixed(2)}` : '—'
      const t = formatDateTime(r.refunded_at || r.created_at || r.at) || ''
      const note = r.note != null ? String(r.note).trim() : ''
      const wx = Math.max(0, Math.round(Number(r.wx_refund_fen) || 0))
      const bal = Math.max(0, Math.round(Number(r.balance_refund_fen) || 0))
      let ch = ''
      if (wx > 0 && bal > 0) ch = `（微信￥${(wx / 100).toFixed(2)}+余额￥${(bal / 100).toFixed(2)}）`
      else if (wx > 0) ch = '（微信）'
      else if (bal > 0) ch = '（余额）'
      const st = formatRefundResultLabel(
        r.refund_result_status || r.wx_refund_status || (wx > 0 ? 'processing' : bal > 0 ? 'success' : '')
      )
      const stPart = st && st !== '—' ? ` [${st}]` : ''
      return `${idx + 1}. ${t} ${amt}${ch}${stPart}${note ? ` ${note}` : ''}`.trim()
    })
    .filter(Boolean)
    .join('\n')
}

const REFUND_RESULT_LABEL_MAP = {
  processing: '退款处理中',
  success: '退款成功',
  closed: '退款关闭',
  abnormal: '退款异常',
  none: '—',
}

function formatRefundResultLabel(status) {
  const s = String(status || '').trim().toLowerCase()
  if (!s) return ''
  return REFUND_RESULT_LABEL_MAP[s] || s
}

function resolveRowRefundResultStatus(row) {
  if (!row) return ''
  const top = String(row.last_refund_result_status || row.last_wx_refund_status || '').trim()
  if (top) return top.toLowerCase()
  const recs = row.refund_records
  if (Array.isArray(recs) && recs.length) {
    const last = recs[recs.length - 1]
    const st = String((last && (last.refund_result_status || last.wx_refund_status)) || '').trim()
    if (st) return st.toLowerCase()
    if (Math.round(Number(last && last.wx_refund_fen) || 0) > 0) return 'processing'
    if (Math.round(Number(last && last.balance_refund_fen) || 0) > 0) return 'success'
  }
  const cr = row.delivery_cancel_request
  if (cr) {
    const st = String(cr.refund_result_status || cr.wx_refund_status || '').trim()
    if (st) return st.toLowerCase()
    if (cr.wx_out_refund_no) return 'processing'
  }
  return ''
}

function refundResultTagColor(status) {
  const s = String(status || '').toLowerCase()
  if (s === 'success') return 'success'
  if (s === 'processing') return 'warning'
  if (s === 'closed' || s === 'abnormal') return 'error'
  return 'default'
}

function canReprintReceipt(row) {
  if (!row || !row._id) return false
  const ds = String(row.delivery_status || '')
  if (ds === 'PENDING_PAYMENT' || ds === 'CANCELLED') return false
  return !!(
    row.paid_at ||
    row.wx_out_trade_no ||
    (row.pay_mes && (row.pay_mes.outTradeNo || Number(row.pay_mes.amount) > 0))
  )
}

function getOrderActions(row) {
  const cr =
    row.delivery_cancel_request && row.delivery_cancel_request.status
  let acts = []
  if (cr === 'pending' || cr === 'processing') {
    acts = [
      {
        key: 'cancel_approve',
        label: '同意取消',
        type: 'error',
        confirm: '同意取消并原路退款？',
      },
      {
        key: 'cancel_reject',
        label: '拒绝取消',
        type: 'default',
        confirm: '拒绝用户的取消申请？',
      },
    ]
  } else {
    const ds = row.delivery_status
    if (ds === 'PENDING_CONFIRM') {
      acts = [
        { key: 'confirm', label: '确认订单', type: 'primary', confirm: '确认接受该订单？' },
      ]
      if (isDineInOrder(row) || !row.delivery_task_id) {
        acts.push({
          key: 'cancel_refund',
          label: '取消退款',
          type: 'error',
          needAmount: true,
        })
      }
    } else if (ds === 'PREPARING') {
      acts = [
        {
          key: 'picking_done',
          label: '出餐完成',
          type: 'primary',
          confirm: '确认出餐完成？',
        },
      ]
      if (isDineInOrder(row) || !row.delivery_task_id) {
        acts.push({
          key: 'cancel_refund',
          label: '取消退款',
          type: 'error',
          needAmount: true,
        })
      }
    } else if (ds === 'WAIT_DISPATCH') {
      acts = [{ key: 'wait_dispatch', label: '待派车', type: 'default', disabled: true }]
    } else if (
      ds === 'DISPATCHING' ||
      ds === 'WAIT_PICKUP' ||
      ds === 'RETURNING_DEPOT'
    ) {
      acts = [{ key: 'in_delivery', label: '配送中', type: 'default', disabled: true }]
    } else if (ds === 'COMPLETED' || ds === 'PARTIAL_REFUNDED') {
      // 已退满（含超退历史单）不再展示退款按钮
      if (formatRemainRefundableFen(row) > 0) {
        acts = [
          {
            key: 'completed_refund',
            label: formatRefundedTotalFen(row) > 0 ? '继续退款' : '退款',
            type: 'error',
            needAmount: true,
          },
        ]
      } else {
        acts = []
      }
    } else if (ds === 'REFUNDED') {
      acts = []
    } else if (ds === 'CANCELLED') {
      acts = []
    } else {
      const typ = Number(row.type)
      if (!ds && typ === 1) {
        acts = [
          {
            key: 'legacy_ship',
            label: '发货',
            type: 'warning',
            confirm: '将该订单标记为运输中？',
          },
        ]
      } else if (!ds && typ === 2) {
        acts = [
          {
            key: 'legacy_deliver',
            label: '送达',
            type: 'success',
            confirm: '确认订单已送达？',
          },
        ]
      }
    }
  }
  if (canReprintReceipt(row)) {
    acts.push({
      key: 'reprint',
      label: '补打',
      type: 'default',
      confirm: '重新打印前厅和后厨小票？',
    })
  }
  return acts
}

export default {
  name: 'FoodOrder',

  data() {
    return {
      todayStats: null,
      refundModalVisible: false,
      refundSubmitting: false,
      refundOkLoading: true,
      refundMode: '',
      refundRow: null,
      refundYuan: null,
      refundMaxYuan: '0.00',
      refundPayHint: '',
      refundNote: '',
      statusFilterOptions: [
        { value: 'PENDING_PAYMENT', label: '待支付' },
        { value: 'PENDING_CONFIRM', label: '待确认' },
        { value: 'PREPARING', label: '制作中/备货中' },
        { value: 'COMPLETED', label: '已完成' },
        { value: 'PARTIAL_REFUNDED', label: '部分退款' },
        { value: 'REFUNDED', label: '已退款' },
        { value: 'CANCELLED', label: '已取消' },
      ],
      columns: [
        { type: 'index', width: 56, align: 'center' },
        {
          title: '下单用户',
          key: 'customer',
          minWidth: 110,
          render: (h, { row }) => {
            const c = row.customer_brief
            if (!c) return h('span', '—')
            const name = c.nickname || '—'
            const phone = c.phone ? `（${c.phone}）` : ''
            return h('div', [h('div', name), phone ? h('div', { style: { color: '#909399', fontSize: '12px' } }, phone) : null])
          },
        },
        {
          title: '下单时间',
          key: 'createtime',
          width: 150,
          render: (h, { row }) => h('span', row.createtime || row.pay_time || '—'),
        },
        {
          title: '用餐方式',
          key: 'fulfillment_mode',
          width: 100,
          align: 'center',
          render: (h, { row }) => {
            const fm = formatFulfillmentMode(row)
            const isDine = fm.label === '堂食'
            const isPack = fm.label === '打包'
            const children = [
              h(
                'Tag',
                { props: { color: isDine ? 'warning' : isPack ? 'blue' : 'default' } },
                fm.label
              ),
            ]
            if (fm.sub) {
              children.push(
                h(
                  'div',
                  { style: { color: '#909399', fontSize: '12px', marginTop: '4px' } },
                  fm.sub
                )
              )
            }
            return h('div', children)
          },
        },
        {
          title: '取餐码',
          key: 'pickup_code',
          width: 90,
          align: 'center',
          render: (h, { row }) => {
            const code = row && row.pickup_code != null ? String(row.pickup_code).trim() : ''
            if (!code) return h('span', { style: { color: '#c0c4cc' } }, '—')
            return h(
              'span',
              { style: { color: '#E87822', fontWeight: '700', fontSize: '16px', letterSpacing: '1px' } },
              code
            )
          },
        },
        {
          title: '商品 / 数量',
          key: 'products',
          minWidth: 180,
          render: (h, { row }) => {
            const children = [h('div', formatProductSummary(row))]
            if (row.bundle_note) {
              children.push(
                h(
                  'div',
                  { style: { color: '#909399', fontSize: '12px', marginTop: '4px' } },
                  row.bundle_note
                )
              )
            }
            return h('div', children)
          },
        },
        {
          title: '订单号',
          key: 'orderNo',
          width: 130,
          render: (h, { row }) => {
            const no =
              (row.pay_mes && row.pay_mes.outTradeNo) ||
              row.wx_out_trade_no ||
              shortId(row._id)
            return h('span', { attrs: { title: String(row._id || '') } }, no)
          },
        },
        {
          title: '金额(元)',
          key: 'amount',
          width: 110,
          align: 'right',
          render: (h, { row }) => {
            const pay = formatPayAmount(row)
            const refunded = formatRefundAmount(row)
            const reserved = formatReservedRefundAmount(row)
            if (!refunded && !reserved) return h('span', pay)
            const children = [h('div', pay === '—' ? pay : `实付 ${pay}`)]
            if (refunded) {
              children.push(
                h('div', { style: { color: '#ed4014', fontSize: '12px' } }, `已退 ${refunded}`)
              )
            }
            if (reserved) {
              children.push(
                h(
                  'div',
                  { style: { color: '#ff9900', fontSize: '12px' } },
                  `处理中 ${reserved}`
                )
              )
            }
            return h('div', children)
          },
        },
        {
          title: '订单状态',
          key: 'status',
          width: 100,
          render: (h, { row }) => {
            const text = orderStatusText(row)
            return h('Tag', { props: { color: orderStatusTagColor(row) } }, text)
          },
        },
        {
          title: '履约备注',
          key: 'remark',
          minWidth: 140,
          render: (h, { row }) => {
            const text = fulfillmentRemarkText(row)
            if (!text) return h('span', { style: { color: '#c5c8ce' } }, '—')
            return h(
              'span',
              {
                attrs: { title: text },
                style: {
                  display: '-webkit-box',
                  '-webkit-line-clamp': 2,
                  '-webkit-box-orient': 'vertical',
                  overflow: 'hidden',
                  wordBreak: 'break-all',
                  lineHeight: '1.4',
                },
              },
              text
            )
          },
        },
        {
          title: '操作',
          key: 'action',
          width: 248,
          align: 'center',
          fixed: 'right',
          render: (h, { row }) => {
            const actions = [
              { key: 'detail', label: '详情', type: 'primary' },
              ...getOrderActions(row),
            ]
            return h(
              'div',
              {
                style: {
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '4px 6px',
                  padding: '4px 0',
                },
              },
              actions.map((act) => {
                const btn = h(
                  'Button',
                  {
                    props: {
                      type: act.type,
                      size: 'small',
                      disabled: act.disabled,
                    },
                    on:
                      act.disabled || act.confirm
                        ? {}
                        : { click: () => this.handleAction(act.key, row) },
                  },
                  act.label
                )
                if (act.confirm && !act.disabled) {
                  return h(
                    'Poptip',
                    {
                      props: {
                        confirm: true,
                        transfer: true,
                        title: act.confirm,
                      },
                      on: {
                        'on-ok': () => this.handleAction(act.key, row),
                      },
                    },
                    [btn]
                  )
                }
                return btn
              })
            )
          },
        },
      ],
      tableData: [],
      total: 0,
      searchInfo: {
        pageNo: 1,
        pageSize: 10,
        key: '',
        deliveryStatus: '',
      },
      pageSizeOpts: [10, 20, 50, 100],
      formItem: {
        key: '',
        deliveryStatus: '',
      },
      tablesHeight: 0,
      detailModalVisible: false,
      detailRow: null,
    }
  },
  computed: {
    refundModalTitle() {
      if (this.refundMode === 'completed_refund') return '出餐完成后退款'
      return '取消订单并退款'
    },
    detailOrderId() {
      const row = this.detailRow
      if (!row) return '—'
      return (
        (row.pay_mes && row.pay_mes.outTradeNo) ||
        row.wx_out_trade_no ||
        String(row._id || '—')
      )
    },
    detailStatusText() {
      return this.detailRow ? orderStatusText(this.detailRow) : '—'
    },
    detailStatusColor() {
      return orderStatusTagColor(this.detailRow)
    },
    detailAmountText() {
      return this.detailRow ? formatPayAmount(this.detailRow) : '—'
    },
    detailRemainRefundText() {
      if (!this.detailRow) return ''
      const remain = formatRemainRefundableFen(this.detailRow)
      const refunded = formatRefundedTotalFen(this.detailRow)
      const reserved = formatReservedRefundFen(this.detailRow)
      if (refunded <= 0 && reserved <= 0) return ''
      return (remain / 100).toFixed(2)
    },
    detailReservedRefundText() {
      if (!this.detailRow) return ''
      return formatReservedRefundAmount(this.detailRow)
    },
    detailRefundRecordsText() {
      return this.detailRow ? formatRefundRecordsText(this.detailRow) : ''
    },
    detailRefundResultText() {
      const st = resolveRowRefundResultStatus(this.detailRow)
      return formatRefundResultLabel(st)
    },
    detailRefundResultColor() {
      return refundResultTagColor(resolveRowRefundResultStatus(this.detailRow))
    },
    detailGoodsLines() {
      return this.detailRow ? formatGoodsDetailLines(this.detailRow) : []
    },
    detailPayBreakdown() {
      return this.detailRow ? buildPayBreakdown(this.detailRow) : []
    },
    detailProductText() {
      return this.detailRow ? formatProductSummary(this.detailRow) : '—'
    },
    detailRemarkText() {
      return this.detailRow ? fulfillmentRemarkText(this.detailRow) : ''
    },
    detailCustomerText() {
      const c = this.detailRow && this.detailRow.customer_brief
      if (!c) return ''
      const name = c.nickname ? String(c.nickname).trim() : ''
      const phone = c.phone ? String(c.phone).trim() : ''
      if (name && phone) return `${name}（${phone}）`
      return name || phone || ''
    },
    detailFulfillmentLabel() {
      if (!this.detailRow) return ''
      const fm = formatFulfillmentMode(this.detailRow)
      if (!fm) return ''
      // 取餐码在顶部单独展示，这里去掉 sub 里的取餐码避免重复
      const mode = String(this.detailRow.fulfillment_mode || '').trim()
      const no =
        this.detailRow.table_no != null ? String(this.detailRow.table_no).trim() : ''
      if (mode === 'dine_in') return no ? `堂食 · ${no} 号桌` : '堂食'
      if (mode === 'pack') return no ? `打包 · ${no} 号桌` : '打包'
      return fm.label !== '其它' ? fm.label : ''
    },
    detailPickupCode() {
      const code =
        this.detailRow && this.detailRow.pickup_code != null
          ? String(this.detailRow.pickup_code).trim()
          : ''
      return code
    },
    detailPayHint() {
      return this.detailRow ? formatPayAmountHint(this.detailRow) : ''
    },
    detailReviewText() {
      return this.detailRow ? formatUserReview(this.detailRow) : ''
    },
    detailReviewTime() {
      return this.detailRow ? formatReviewTime(this.detailRow) : ''
    },
    detailRefundNote() {
      return this.detailRow ? formatRefundNote(this.detailRow) : ''
    },
    detailRefundTime() {
      return this.detailRow ? formatRefundTime(this.detailRow) : ''
    },
    detailConfirmedRefundAmount() {
      return this.detailRow ? formatRefundAmount(this.detailRow) : ''
    },
    detailRefundActionAmount() {
      return this.detailRow ? formatRefundActionAmount(this.detailRow) : ''
    },
  },
  methods: {
    setTablesHeight() {
      const oA = document.getElementById('app')
      const sA = document.getElementById('searchForm')
      const oH = oA ? oA.clientHeight || oA.offsetHeight : 600
      const sH = sA ? sA.clientHeight || sA.offsetHeight : 80
      this.tablesHeight = Math.max(320, oH - sH - 265)
    },

    loadTodayStats() {
      getFoodTodayStats()
        .then((res) => {
          const body = res && res.data
          const ok = body && (body.success === true || body.code === 200)
          const data = body && body.data
          if (ok && data) {
            this.todayStats = {
              today_order_count: Number(data.today_order_count) || 0,
              today_revenue_yuan: Number(data.today_revenue_yuan) || 0,
              pending_confirm: Number(data.pending_confirm) || 0,
              date: data.date || '',
            }
          }
        })
        .catch(() => {})
    },

    getList() {
      const payload = { ...this.searchInfo }
      if (!payload.key) delete payload.key
      if (!payload.deliveryStatus) {
        delete payload.deliveryStatus
      } else {
        payload.delivery_status = payload.deliveryStatus
        delete payload.deliveryStatus
      }
      getfoodOrderList(payload)
        .then((res) => {
          if (res.data.code === 200) {
            this.tableData = res.data.data || []
            this.total = res.data.total || 0
          } else {
            this.$Message.error(res.data.msg || '获取订单列表失败')
          }
        })
        .catch((err) => {
          this.$Message.error(err && err.message ? err.message : '获取订单列表失败')
        })
    },

    search() {
      this.searchInfo.pageNo = 1
      this.searchInfo.key = this.formItem.key
      this.searchInfo.deliveryStatus = this.formItem.deliveryStatus
      this.getList()
    },

    clear() {
      this.formItem = { key: '', deliveryStatus: '' }
      this.searchInfo = {
        pageNo: 1,
        pageSize: 10,
        key: '',
        deliveryStatus: '',
      }
      this.getList()
    },

    openRefundModal(mode, row) {
      const remainFen =
        mode === 'completed_refund'
          ? formatRemainRefundableFen(row)
          : formatPayAmountFen(row)
      const max = remainFen > 0 ? remainFen / 100 : 0
      this.refundMode = mode
      this.refundRow = row
      this.refundMaxYuan = max > 0 ? max.toFixed(2) : '0.00'
      const paidText = formatPayAmount(row)
      const refundedFen = formatRefundedTotalFen(row)
      const reservedFen = formatReservedRefundFen(row)
      let hint = formatPayAmountHint(row)
      if (mode === 'completed_refund' && (refundedFen > 0 || reservedFen > 0)) {
        const parts = []
        if (refundedFen > 0) parts.push(`已退￥${(refundedFen / 100).toFixed(2)}`)
        if (reservedFen > 0) parts.push(`处理中￥${(reservedFen / 100).toFixed(2)}`)
        parts.push(`可退￥${max.toFixed(2)}（实付￥${paidText}）`)
        const extra = parts.join('，')
        hint = hint ? `${hint}；${extra}` : extra
      }
      this.refundPayHint = hint
      this.refundYuan = max > 0 ? max : null
      this.refundNote = ''
      this.refundOkLoading = true
      this.refundModalVisible = true
    },

    fillFullRefund() {
      const max = Number(this.refundMaxYuan)
      if (Number.isFinite(max) && max > 0) this.refundYuan = max
    },

    closeRefundModal() {
      this.refundModalVisible = false
      this.refundSubmitting = false
      this.refundOkLoading = true
      this.refundRow = null
      this.refundMode = ''
    },

    keepRefundModalOpen() {
      this.refundOkLoading = false
      this.$nextTick(() => {
        this.refundOkLoading = true
      })
    },

    submitRefundModal() {
      // InputNumber 未失焦时 v-model 可能仍是打开弹窗时的全额
      if (typeof document !== 'undefined' && document.activeElement) {
        try {
          document.activeElement.blur()
        } catch (e) {}
      }
      this.$nextTick(() => {
        this.doSubmitRefundModal()
      })
    },

    doSubmitRefundModal() {
      if (this.refundSubmitting) return
      const row = this.refundRow
      if (!row || !row._id) {
        this.closeRefundModal()
        return
      }
      if (this.refundMode === 'completed_refund' && formatRemainRefundableFen(row) <= 0) {
        this.$Message.warning('该订单已全额退款，不可再退')
        this.closeRefundModal()
        this.getList()
        return
      }
      const yuan = Number(this.refundYuan)
      const max = Number(this.refundMaxYuan)
      if (!Number.isFinite(yuan) || yuan <= 0) {
        this.$Message.warning('请输入有效退款金额')
        this.keepRefundModalOpen()
        return
      }
      if (Number.isFinite(max) && max > 0 && yuan > max + 0.0001) {
        this.$Message.warning('退款金额不能超过可退金额')
        this.keepRefundModalOpen()
        return
      }
      const note = String(this.refundNote || '').trim()
      if (!note) {
        this.$Message.warning('请填写退款说明')
        this.keepRefundModalOpen()
        return
      }
      const fen = Math.round(Number(yuan.toFixed(2)) * 100)
      if (!Number.isFinite(fen) || fen <= 0) {
        this.$Message.warning('请输入有效退款金额')
        this.keepRefundModalOpen()
        return
      }
      const apiFn =
        this.refundMode === 'completed_refund'
          ? adminRefundFoodOrder
          : adminCancelAndRefundFoodOrder
      const okMsg =
        this.refundMode === 'completed_refund'
          ? '退款已受理'
          : '已取消并受理退款'
      this.refundSubmitting = true
      apiFn({
        _id: row._id,
        refund_yuan: yuan,
        refund_fen: fen,
        note,
      })
        .then((res) => {
          const body = res && res.data
          const code = body && typeof body === 'object' ? Number(body.code) : NaN
          if (code === 200) {
            const d = body.data || {}
            const bal = Math.round(Number(d.balance_refund_fen) || 0)
            const wx = Math.round(Number(d.wx_refund_fen) || 0)
            const rf = Math.round(Number(d.refund_fen) || 0) || bal + wx
            const st = String(d.refund_result_status || '').toLowerCase()
            let amt = `￥${yuan.toFixed(2)}`
            if (rf > 0) amt = `￥${(rf / 100).toFixed(2)}`
            let channel = ''
            if (st === 'success' || (!wx && bal > 0)) {
              if (bal > 0 && wx > 0) channel = '（微信+余额已到账）'
              else if (bal > 0) channel = '（已退回余额）'
              else channel = '（已成功）'
            } else if (wx > 0) {
              channel =
                bal > 0
                  ? '（余额已退；微信处理中，成功后更新已退/营业额）'
                  : '（微信处理中，成功后更新已退/营业额）'
            }
            this.$Message.success((body.msg || okMsg) + ` ${amt}${channel}`)
            this.closeRefundModal()
            this.getList()
            this.loadTodayStats()
          } else {
            const msg =
              (body && typeof body === 'object' && body.msg) ||
              (res && res.status === 404
                ? '接口不存在，请重启后端后再试'
                : null) ||
              '操作失败'
            this.$Message.error(msg)
            this.refundSubmitting = false
            this.keepRefundModalOpen()
          }
        })
        .catch((err) => {
          const msg =
            (typeof err === 'string' && err) ||
            (err && err.message) ||
            (err && err.msg) ||
            '操作失败'
          this.$Message.error(msg)
          this.refundSubmitting = false
          this.keepRefundModalOpen()
        })
    },

    openDetailModal(row) {
      this.detailRow = row || null
      this.detailModalVisible = true
    },

    copyDetailOrderId() {
      const text = this.detailOrderId
      if (!text || text === '—') return
      const done = () => this.$Message.success('订单号已复制')
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(() => {
          this.fallbackCopyText(text)
          done()
        })
        return
      }
      this.fallbackCopyText(text)
      done()
    },

    fallbackCopyText(text) {
      const el = document.createElement('textarea')
      el.value = text
      el.setAttribute('readonly', '')
      el.style.position = 'absolute'
      el.style.left = '-9999px'
      document.body.appendChild(el)
      el.select()
      try {
        document.execCommand('copy')
      } catch (e) {}
      document.body.removeChild(el)
    },

    handleAction(key, row) {
      if (key === 'detail') {
        this.openDetailModal(row)
        return
      }
      if (key === 'confirm') {
        this.runAdminAction(adminConfirmFoodOrder, row._id, '确认成功')
        return
      }
      if (key === 'picking_done') {
        this.runAdminAction(adminPickingDoneFoodOrder, row._id, '操作成功')
        return
      }
      if (key === 'cancel_refund') {
        this.openRefundModal('cancel_refund', row)
        return
      }
      if (key === 'completed_refund') {
        this.openRefundModal('completed_refund', row)
        return
      }
      if (key === 'cancel_approve') {
        this.runAdminAction(adminCancelApproveFoodOrder, row._id, '已同意取消')
        return
      }
      if (key === 'cancel_reject') {
        this.runAdminAction(adminCancelRejectFoodOrder, row._id, '已拒绝取消')
        return
      }
      if (key === 'legacy_ship') {
        this.runLegacyType(row._id, 2)
        return
      }
      if (key === 'legacy_deliver') {
        this.runLegacyType(row._id, 3)
        return
      }
      if (key === 'reprint') {
        this.runAdminAction(adminReprintFoodOrder, row._id, '已下发补打')
      }
    },

    runLegacyType(_id, type) {
      updateordertype({ _id, type })
        .then((res) => {
          if (res.data.code === 200) {
            this.$Message.success('状态已更新')
            this.getList()
          } else {
            this.$Message.error(res.data.msg || '操作失败')
          }
        })
        .catch((err) => {
          this.$Message.error(err && err.message ? err.message : '操作失败')
        })
    },

    runAdminAction(apiFn, _id, okMsg) {
      apiFn({ _id })
        .then((res) => {
          if (res.data.code === 200) {
            this.$Message.success(okMsg)
            this.getList()
            this.loadTodayStats()
          } else {
            this.$Message.error(res.data.msg || '操作失败')
          }
        })
        .catch((err) => {
          this.$Message.error(err && err.message ? err.message : '操作失败')
        })
    },

    pageNoChange(current) {
      this.searchInfo.pageNo = current
      this.getList()
    },

    pageSizeChange(val) {
      this.searchInfo.pageNo = 1
      this.searchInfo.pageSize = val
      this.getList()
    },
  },
  mounted() {
    this.getList()
    this.loadTodayStats()
    setTimeout(() => this.setTablesHeight(), 50)
    window.onresize = () => this.setTablesHeight()
  },
}
</script>
<style lang="less" scoped>
.goodsBox {
  .today-stats-alert {
    margin-bottom: 12px;
  }
  .ivu-form-inline .ivu-form-item {
    width: auto;
    min-width: 200px;
  }
}

.order-detail {
  .pay-section-title {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 8px;
  }

  .goods-line {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    line-height: 1.6;

    &__amt {
      flex-shrink: 0;
      color: #606266;
    }
  }

  .info-item {
    padding: 8px 0;

    &--em {
      padding-top: 12px;
      margin-top: 4px;
      border-top: 1px dashed #ebeef5;
    }

    .label {
      color: #909399;
      width: 120px;
      display: inline-block;
      vertical-align: top;
    }

    .value {
      color: #303133;
      display: inline-block;
      max-width: calc(100% - 130px);
      word-break: break-all;
      vertical-align: top;
    }

    .value--disc {
      color: #e87822;
    }

    .value--total {
      color: #ed4014;
      font-weight: 600;
      font-size: 15px;
    }
  }
}
</style>
